import { useState, useEffect } from "react";
import { api } from "./api";
import { formatMinor } from "./commercial";
import i18n, { intlLocaleFor } from "../i18n";

// Source de vérité commerciale : GET /api/plans (régionalisable). Cache mémoire par région, MAIS
// publication-aware : à chaque montage et au focus fenêtre, on refetch ; si la publication active a
// changé (nouveau publication_id), le cache est remplacé → aucun prix obsolète sans hard refresh.
let _cache = {};

export const usePlans = (region) => {
  const key = region || "_default";
  const [data, setData] = useState(_cache[key] || null);
  const [loading, setLoading] = useState(!_cache[key]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    if (_cache[key]) setData(_cache[key]);

    const fetchPlans = () => {
      const q = region ? `/plans?region=${encodeURIComponent(region)}` : "/plans";
      api
        .get(q)
        .then(({ data: d }) => {
          if (!active) return;
          const prev = _cache[key];
          // Remplacement immédiat si première charge OU republication détectée.
          if (!prev || prev.publication_id !== d.publication_id || JSON.stringify(prev.plans) !== JSON.stringify(d.plans)) {
            _cache[key] = d;
            setData(d);
          }
          setLoading(false);
          setError(false);
        })
        .catch(() => {
          if (!active) return;
          if (!_cache[key]) setError(true);
          setLoading(false);
        });
    };

    fetchPlans();
    const onFocus = () => fetchPlans();
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [key, region]);

  return { plans: data?.plans || null, data, loading, error };
};

// ---- Formatage commercial (présentation uniquement) ----
export const fmtInt = (n) => (n == null ? "" : Number(n).toLocaleString(intlLocaleFor(i18n.language)));

// Montant en minor units -> chaîne localisée avec devise. Délègue au helper monétaire CENTRAL
// (lib/commercial.js) : décimales dérivées de la devise (XPF/EUR/AUD/USD…), fin du "XPF?F CFP:€".
export const fmtAmountMinor = (minor, currency = "EUR") => {
  if (minor == null) return null;
  return formatMinor(minor, currency);
};

export const fmtExpiration = (e) => {
  if (!e) return "—";
  const m = String(e).match(/^(\d+)\s*([hd])$/i);
  if (!m) return e;
  const n = parseInt(m[1], 10);
  return m[2].toLowerCase() === "h"
    ? i18n.t("units.hours", { count: n })
    : i18n.t("units.days", { count: n });
};

export const fmtHistory = (days) => {
  if (days == null) return i18n.t("units.custom");
  if (days % 365 === 0) {
    const y = days / 365;
    return i18n.t("units.years", { count: y });
  }
  return i18n.t("units.days", { count: days });
};
